/**
 * ObjectActionsPage
 *
 * Drill-down page showing available actions for a selected database object.
 * Uses the action registry to get actions for the object type.
 */

import { CommandGroup, CommandItem } from '@/components/ui/command';
import { getActionsForObject, executeAction } from '../../shared/object-actions/actionRegistry';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import type { SelectedObject } from './DatabaseObjectSearch';

type ObjectActionsPageProps = {
  selected: SelectedObject;
};

export function ObjectActionsPage({ selected }: ObjectActionsPageProps) {
  const close = useCommandPaletteStore((s) => s.close);
  const actions = getActionsForObject(selected.schemaObjectType);

  const context = {
    connectionId: selected.object.connectionId,
    databaseName: selected.database,
    schemaName: selected.object.schema,
    objectName: selected.object.name,
    objectType: selected.schemaObjectType,
    objectId: selected.object.id,
  };

  const handleSelect = (actionId: string) => {
    close();
    setTimeout(() => executeAction(actionId, context), 0);
  };

  return (
    <CommandGroup heading={`${selected.object.schema}.${selected.object.name}`}>
      {actions.map((action) => {
        const Icon = action.icon;
        const isDisabled = action.isEnabled ? !action.isEnabled(context) : false;

        return (
          <CommandItem
            key={action.id}
            value={action.id}
            keywords={[action.label, action.category]}
            disabled={isDisabled}
            onSelect={() => handleSelect(action.id)}
          >
            {Icon && <Icon className="mr-2 h-4 w-4 shrink-0 opacity-70" />}
            <span className="flex-1">{action.label}</span>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
